import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";

/**
 * Monetag (or any) ad tag injection. Admin pastes the provider snippet in Admin → Sozlamalar → Reklama.
 * <script> tags are re-created so the browser executes them.
 */
export function injectCode(code, target) {
  if (!code || !target) return [];
  const tpl = document.createElement("template");
  tpl.innerHTML = code.trim();
  const added = [];
  tpl.content.childNodes.forEach((node) => {
    let el;
    if (node.nodeName === "SCRIPT") {
      el = document.createElement("script");
      [...node.attributes].forEach((a) => el.setAttribute(a.name, a.value));
      el.text = node.textContent;
      el.async = true;
    } else el = node.cloneNode(true);
    target.appendChild(el);
    added.push(el);
  });
  return added;
}

const GLOBAL = ["popunder", "inpage_push", "vignette"];
const injected = new Set();

/** Site-wide ad formats (popunder / in-page push / vignette). Never on admin or player screens. */
export function AdsManager() {
  const { settings } = useSettings();
  const { pathname } = useLocation();
  useEffect(() => {
    const p = settings?.ads?.placements;
    if (!p || pathname.startsWith("/admin") || pathname.startsWith("/login")) return;
    const t = setTimeout(() => {
      GLOBAL.forEach((k) => {
        if (p[k]?.enabled && p[k].code && !injected.has(k)) {
          injected.add(k);
          injectCode(p[k].code, document.body);
        }
      });
    }, 2500); // never block first paint
    return () => clearTimeout(t);
  }, [settings, pathname]);
  return null;
}

/** Inline banner slot (banner_home / banner_detail). */
export function AdSlot({ name, className = "" }) {
  const { settings } = useSettings();
  const ref = useRef(null);
  const p = settings?.ads?.placements?.[name];
  useEffect(() => {
    const el = ref.current;
    if (!el || !p?.enabled || !p.code) return;
    el.innerHTML = "";
    injectCode(p.code, el);
    return () => {
      el.innerHTML = "";
    };
  }, [p?.enabled, p?.code]);
  if (!p?.enabled || !p.code) return null;
  return (
    <div className={`pk-container my-4 ${className}`} data-testid={`ad-slot-${name}`}>
      <p className="mb-1 text-[11px] font-semibold text-white/30">Reklama</p>
      <div ref={ref} className="flex min-h-[60px] justify-center overflow-hidden rounded-[16px]" />
    </div>
  );
}

const LAST_KEY = "pk_preroll_at";

/** Pre-roll gate shown before video start; respects frequency cap. Calls onDone when finished/skipped. */
export function usePreroll() {
  const { settings } = useSettings();
  const p = settings?.ads?.placements?.video_start;
  const [state] = useState(() => {
    if (!p?.enabled || !p.direct_link) return false;
    const last = Number(localStorage.getItem(LAST_KEY) || 0);
    return Date.now() - last > (p.frequency_minutes || 0) * 60 * 1000;
  });
  return state ? p : null;
}

export function PrerollAd({ config, onDone }) {
  const [left, setLeft] = useState(config.skip_after || 0);
  useEffect(() => {
    localStorage.setItem(LAST_KEY, String(Date.now()));
    if (left <= 0) return;
    const t = setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return (
    <div className="absolute inset-0 z-[90] grid place-items-center bg-black/85 p-4" data-testid="preroll-ad">
      <div className="pk-glass-strong w-full max-w-sm rounded-[26px] p-7 text-center">
        <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11.5px] font-bold text-white/70">Reklama</span>
        <h2 className="mt-4 text-[20px] font-extrabold text-white">Video reklamadan so‘ng boshlanadi</h2>
        <p className="mt-2 text-[14px] text-white/55">Homiylarimiz tufayli kontent bepul.</p>
        <a href={config.direct_link} target="_blank" rel="noopener noreferrer sponsored" className="mt-6 flex h-12 items-center justify-center rounded-[16px] bg-white text-[15.5px] font-bold text-black active:scale-[.97]" data-testid="preroll-open">
          Reklamani ko‘rish
        </a>
        <button disabled={left > 0} onClick={onDone} className="mt-3 h-12 w-full rounded-[16px] border border-white/15 text-[15px] font-semibold text-white disabled:opacity-50" data-testid="preroll-skip">
          {left > 0 ? `O‘tkazib yuborish (${left})` : "O‘tkazib yuborish"}
        </button>
      </div>
    </div>
  );
}
